import React, { useEffect, useMemo, useState } from 'react';
import { apv as api } from '../services/api';
import { nb, eur, montant, moisCourt, useFiltresVentes, PanneauVentes, usePanneau, SelecteurUnite, useCanal } from './ventesUtils';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles } from './regroupements';
import './Ventes.css';
import './Stock.css';
import './FacturationType.css';

// ─── APV — Facturation par type (Auto Métropole) ────────────────────────────
// Montants facturés par concession et par mois, ventilés en Payant / Cession /
// Garantie / Assurance, puis détail d'un type jusqu'à la liste des OR.
// Le type est porté sur chaque ligne de l'export Sellens (M.O., pièces,
// trav. ext.) : aucune règle de rattachement, contrairement aux Antilles.
//
// Trois gestes, sur trois cibles distinctes (CLAUDE.md §4 bis) :
//   · le LIBELLÉ d'une ligne déplie (regroupement → concessions, concession →
//     ses quatre types quand « Tous types » est choisi) ;
//   · une CASE (concession × mois, ou le total) ouvre son détail dans le bloc
//     du dessous — c'est un dévoilement, pas un filtre : le panneau reste seul
//     maître du périmètre ;
//   · une ligne du détail ouvre la liste de ses OR, comme « OR à traiter ».

const TYPES = ['Payant', 'Cession', 'Garantie', 'Assurance'];
const PRESTATIONS = [
  { code: '', label: 'Toutes' },
  { code: 'mo', label: 'M.O.', val: 'M.O.' },
  { code: 'pieces', label: 'Pièces', val: 'Pièces' },
  { code: 'te', label: 'Trav. ext.', val: 'Trav.Ext.' },
];
// `seul` : axe proposé pour ce seul type ; `tous` : seulement sans type choisi.
const AXES = [
  { c: 'destinataire', l: 'Destinataire', seul: 'Cession' },
  { c: 'type', l: 'Type', tous: true },
  { c: 'concession', l: 'Concession' },
  { c: 'atelier', l: 'Atelier' },
  { c: 'prestation', l: 'Prestation' },
  { c: 'marque', l: 'Marque' },
  { c: 'receptionnaire', l: 'Réceptionnaire' },
];
const axesPour = (type) => AXES.filter((a) => (a.seul ? a.seul === type : a.tous ? !type : true));
const axeDefaut = (type) => (type === 'Cession' ? 'destinataire' : !type ? 'type' : 'concession');
const libPrestation = (p) => (p === 'Trav.Ext.' ? 'Trav. ext.' : p);
const pct = (a, b) => (b ? `${Math.round((a / b) * 100)} %` : '—');
const somme = (arr) => arr.reduce((a, b) => a + b, 0);
const dateFr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

export default function FacturationType() {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [type, setType] = useState('');              // '' = tous types
  const [prestation, setPrestation] = useState('');
  const [unite, setUnite] = useState('k');
  const [ouverts, setOuverts] = useState(() => new Set());   // concessions dépliées en types
  const [focus, setFocus] = useState(null);         // { codes, libelle, ym, type }
  const [axe, setAxe] = useState('type');
  const [groupe, setGroupe] = useState(null);       // { cle, libelle }
  const [detail, setDetail] = useState(null);
  const [liste, setListe] = useState(null);
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);
  const { canal, choix } = useCanal(f);
  const maille = useRegroupement();

  useEffect(() => {
    let actif = true; setErreur('');
    api.facturationType({ periode: f.periode, mois: f.moisStr, societes: f.societesUrl, canal })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [f.periode, f.moisStr, f.societesUrl, canal]);

  // Un changement de périmètre referme le détail ouvert : il ne décrirait plus
  // ce que montre la matrice.
  useEffect(() => { setFocus(null); setGroupe(null); }, [f.periode, f.moisStr, f.societesUrl, canal]);

  // Type du détail : celui de la case cliquée (ligne de type), sinon celui de la page.
  const typeDetail = focus?.type ?? type;
  const axesDispo = axesPour(typeDetail);
  const axeCourant = axesDispo.some((a) => a.c === axe) ? axe : axeDefaut(typeDetail);
  const prestVal = PRESTATIONS.find((p) => p.code === prestation)?.val;

  // Paramètres communs au détail et à la liste des OR.
  const paramsDetail = useMemo(() => ({
    periode: f.periode,
    mois: focus?.ym || f.moisStr,
    societes: focus ? focus.codes.join(',') : f.societesUrl,
    canal, type: typeDetail, prestation,
  }), [f.periode, f.moisStr, f.societesUrl, focus, canal, typeDetail, prestation]);

  useEffect(() => {
    if (!data) return undefined;
    let actif = true; setDetail(null);
    api.facturationTypeDetail(paramsDetail)
      .then((r) => { if (actif) setDetail(r.data); })
      .catch(() => { if (actif) setDetail({ erreur: true }); });
    return () => { actif = false; };
  }, [data, paramsDetail]);

  useEffect(() => { setGroupe(null); }, [paramsDetail, axeCourant]);

  useEffect(() => {
    if (!groupe) { setListe(null); return undefined; }
    let actif = true; setListe(null);
    api.facturationTypeOrs({ ...paramsDetail, axe: axeCourant, cle: groupe.cle })
      .then((r) => { if (actif) setListe(r.data); })
      .catch(() => { if (actif) setListe({ erreur: true }); });
    return () => { actif = false; };
  }, [groupe, paramsDetail, axeCourant]);

  // ── Pivot : société → type → mois, pour la prestation choisie ──
  const mois = useMemo(() => data?.mois || [], [data]);
  const feuilles = useMemo(() => {
    if (!data) return [];
    const idx = new Map(mois.map((m, i) => [m, i]));
    const par = new Map();
    for (const l of data.lignes) {
      if (prestVal && l.prestation !== prestVal) continue;
      const i = idx.get(l.ym); if (i === undefined) continue;
      if (!par.has(l.societe)) par.set(l.societe, Object.fromEntries(TYPES.map((t) => [t, mois.map(() => 0)])));
      const p = par.get(l.societe);
      if (p[l.type]) p[l.type][i] += l.montant;
    }
    return societesDispo.filter((s) => f.selected.includes(s.code) && par.has(s.code)).map((s) => {
      const parType = par.get(s.code);
      const tot = mois.map((_, i) => somme(TYPES.map((t) => parType[t][i])));
      return { ...s, libelle: s.nom, parType, tot, v: type ? parType[type] : tot };
    });
  }, [data, mois, prestVal, societesDispo, f.selected, type]);

  const sommer = (membres) => {
    const parType = Object.fromEntries(TYPES.map((t) => [t, mois.map((_, i) => somme(membres.map((m) => m.parType[t][i])))]));
    const tot = mois.map((_, i) => somme(membres.map((m) => m.tot[i])));
    return { parType, tot, v: type ? parType[type] : tot };
  };
  const arbre = useMemo(
    () => construireArbre(feuilles, maille.niveau, sommer, maille.ouverts, (x) => somme(x.v)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feuilles, maille.niveau, maille.ouverts]);
  const axes = useMemo(() => axesDisponibles(feuilles), [feuilles]);
  const total = useMemo(() => sommer(feuilles), [feuilles]); // eslint-disable-line react-hooks/exhaustive-deps

  const Panneau = <PanneauVentes ouvert={ouvert} onToggle={toggle} f={f} societesDispo={societesDispo} extra={{ choix }} />;
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const totGen = somme(total.tot);
  const fmt = (v) => (v ? montant(v, unite) : '·');
  const toggleTypes = (code) => setOuverts((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const estFocus = (codes, ym, t) => focus && focus.ym === ym && (focus.type ?? null) === (t ?? null)
    && focus.codes.length === codes.length && codes.every((c) => focus.codes.includes(c));
  const ouvrir = (codes, libelle, ym, t) => setFocus(estFocus(codes, ym, t) ? null : { codes, libelle, ym, type: t });

  // Cases d'une ligne : les mois, le total, la part.
  const Cases = ({ vals, codes, libelle, t, base }) => (
    <>
      {vals.map((v, i) => (
        <td key={mois[i]} className={`ft-case${estFocus(codes, mois[i], t) ? ' on' : ''}`}
          onClick={() => ouvrir(codes, libelle, mois[i], t)} title="Voir le détail">{fmt(v)}</td>
      ))}
      <td className={`ft-case tot${estFocus(codes, null, t) ? ' on' : ''}`}
        onClick={() => ouvrir(codes, libelle, null, t)} title="Voir le détail">{fmt(somme(vals))}</td>
      <td className="ft-part">{pct(somme(vals), base)}</td>
    </>
  );

  // Lignes de type sous une concession (mode « Tous types »).
  const lignesTypes = (x, codes, niveau) => TYPES.map((t) => (
    <tr key={`${x.cle}/${t}`} className="ft-sub">
      <td className="l"><span className="ft-indent" style={{ width: (niveau + 1) * 18 + 14 }} />{t}</td>
      <Cases vals={x.parType[t]} codes={codes} libelle={x.libelle} t={t} base={somme(x.tot)} />
    </tr>
  ));

  const K = [
    { l: 'Facturé', v: totGen, d: data.periode },
    ...TYPES.map((t) => ({ l: t, v: somme(total.parType[t]), d: `${pct(somme(total.parType[t]), totGen)} du facturé` })),
  ];
  const typeLib = type ? type.toLowerCase() : 'tous types';
  const lignesDetail = detail?.axes?.[axeCourant] || [];
  const totDetail = lignesDetail.reduce((a, r) => ({ mo: a.mo + r.mo, pieces: a.pieces + r.pieces, te: a.te + r.te, total: a.total + r.total }), { mo: 0, pieces: 0, te: 0, total: 0 });
  const libGroupe = (r) => (axeCourant === 'prestation' ? libPrestation(r.cle) : r.libelle || r.cle);
  const nomPerimetre = focus
    ? `${focus.libelle}${focus.type ? ` · ${focus.type}` : ''} · ${focus.ym ? moisCourt(focus.ym) + ' ' + focus.ym.slice(0, 4) : data.periode}`
    : null;

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div><h1>APV — Facturation par type</h1></div>
          <div className="vt-asof"><b>{data.periode}</b></div>
        </div>

        {f.selected.length === 0 ? (
          <div className="bi-empty"><h2>Aucune société sélectionnée</h2><p>Cochez au moins une société dans les filtres à droite.</p></div>
        ) : (
          <>
            <div className="vt-kpis" style={{ gridTemplateColumns: 'repeat(5,1fr)' }}>
              {K.map((k) => (
                <div className="vt-kpi" key={k.l}>
                  <div className="l">{k.l}</div>
                  <div className="v">{montant(k.v, 'k')}</div>
                  <div className="d"><small>{k.d}</small></div>
                </div>
              ))}
            </div>

            <div className="vt-ctl">
              <span><span className="lab">Type</span>
                <span className="vt-seg">
                  <button type="button" className={!type ? 'on' : ''} onClick={() => setType('')}>Tous types</button>
                  {TYPES.map((t) => <button key={t} type="button" className={type === t ? 'on' : ''} onClick={() => setType(t)}>{t}</button>)}
                </span>
              </span>
              <span><span className="lab">Prestation</span>
                <span className="vt-seg">{PRESTATIONS.map((p) => (
                  <button key={p.code} type="button" className={prestation === p.code ? 'on' : ''} onClick={() => setPrestation(p.code)}>{p.label}</button>
                ))}</span>
              </span>
            </div>

            {/* ── Bloc 1 : concession × mois ── */}
            <div className="vt-card">
              <div className="vt-card-h">
                <h2>Facturé par concession et par mois — {typeLib}</h2>
                <div className="st-tools">
                  <span className="hint">{type ? `part = poids du type dans le facturé de la ligne` : 'part = poids dans le total'}</span>
                  <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axes} />
                  <SelecteurUnite unite={unite} setUnite={setUnite} />
                </div>
              </div>
              <div className="vt-card-b vt-tbl-wrap">
                {feuilles.length === 0 ? <p className="ft-vide">Aucune facturation sur ce périmètre.</p> : (
                  <table className="vt-tbl ft-tbl tbl-dense">
                    <thead><tr>
                      <th className="l">Concession</th>
                      {mois.map((m) => <th key={m}>{moisCourt(m)}</th>)}
                      <th className="tot">Total</th><th>Part</th>
                    </tr></thead>
                    <tbody>
                      {arbre.map((r) => {
                        if (!r.feuille) {
                          const on = maille.ouverts.has(r.cle);
                          return (
                            <tr key={r.cle} className="ft-grp">
                              <td className="l ft-lib" onClick={() => maille.basculer(r.cle)}>
                                <span className="ft-indent" style={{ width: r.niveau * 18 }} />
                                <span className={`ft-chev${on ? ' on' : ''}`}>▸</span>{r.libelle}
                                <span className="ft-nb">{r.nbEnfants} concessions</span>
                              </td>
                              <Cases vals={r.v} codes={r.membres} libelle={r.libelle} t={null} base={type ? somme(r.tot) : totGen} />
                            </tr>
                          );
                        }
                        const on = !type && ouverts.has(r.code);
                        return (
                          <React.Fragment key={r.cle}>
                            <tr>
                              <td className={`l${!type ? ' ft-lib' : ''}`} onClick={!type ? () => toggleTypes(r.code) : undefined}>
                                <span className="ft-indent" style={{ width: r.niveau * 18 }} />
                                <span className={`ft-chev${on ? ' on' : ''}${type ? ' vide' : ''}`}>▸</span>{r.libelle}
                              </td>
                              <Cases vals={r.v} codes={[r.code]} libelle={r.libelle} t={null} base={type ? somme(r.tot) : totGen} />
                            </tr>
                            {on && lignesTypes(r, [r.code], r.niveau)}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot><tr>
                      <td className="l">Total</td>
                      {total.v.map((v, i) => <td key={mois[i]}>{fmt(v)}</td>)}
                      <td className="tot">{fmt(somme(total.v))}</td>
                      <td>{pct(somme(total.v), totGen)}</td>
                    </tr></tfoot>
                  </table>
                )}
              </div>
            </div>

            {/* ── Bloc 2 : détail d'un type ── */}
            <div className="vt-card">
              <div className="vt-card-h">
                <h2>Détail — {typeDetail ? typeDetail.toLowerCase() : 'tous types'}</h2>
                <div className="st-tools">
                  <span className="st-seg">
                    {axesDispo.map((a) => (
                      <button key={a.c} type="button" className={axeCourant === a.c ? 'on' : ''} onClick={() => setAxe(a.c)}>{a.l}</button>
                    ))}
                  </span>
                </div>
              </div>
              <div className="st-sous-h">
                {nomPerimetre ? (
                  <span className="st-puce">Case : <b>{nomPerimetre}</b>
                    <button type="button" onClick={() => setFocus(null)} aria-label="Revenir à tout le périmètre" title="Revenir à tout le périmètre">×</button>
                  </span>
                ) : <span className="ft-hint">Tout le périmètre · {data.periode}</span>}
                {axeCourant === 'destinataire' && <span className="ft-hint">Livraison VN / VO = OR au nom de l'acheteur, rattaché au véhicule vendu par le VIN</span>}
              </div>
              <div className="vt-card-b vt-tbl-wrap">
                {!detail ? <p className="ft-vide">Chargement…</p>
                  : detail.erreur ? <p className="ft-vide">Détail indisponible.</p>
                  : lignesDetail.length === 0 ? <p className="ft-vide">Aucune facturation sur ce périmètre.</p> : (
                    <table className="vt-tbl tbl-dense">
                      <thead><tr>
                        <th className="l">{AXES.find((a) => a.c === axeCourant).l}</th>
                        <th>M.O.</th><th>Pièces</th><th>Trav. ext.</th><th className="tot">Total</th><th>Part</th>
                      </tr></thead>
                      <tbody>
                        {lignesDetail.map((r) => {
                          const actif = groupe && groupe.cle === r.cle;
                          return (
                            <tr key={r.cle} className={`tbl-clic${actif ? ' actif' : ''}`} title="Voir ces OR"
                              onClick={() => setGroupe(actif ? null : { cle: r.cle, libelle: libGroupe(r) })}>
                              <td className="l">{libGroupe(r)}</td>
                              <td>{fmt(r.mo)}</td><td>{fmt(r.pieces)}</td><td>{fmt(r.te)}</td>
                              <td className="tot">{fmt(r.total)}</td>
                              <td className="ft-part">{pct(r.total, totDetail.total)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot><tr>
                        <td className="l">Total</td>
                        <td>{fmt(totDetail.mo)}</td><td>{fmt(totDetail.pieces)}</td><td>{fmt(totDetail.te)}</td>
                        <td className="tot">{fmt(totDetail.total)}</td><td>100 %</td>
                      </tr></tfoot>
                    </table>
                  )}
              </div>

              {groupe && (
                <>
                  <div className="st-sous-h ft-liste-h">
                    <span className="st-puce">{AXES.find((a) => a.c === axeCourant).l} : <b>{groupe.libelle}</b>
                      <button type="button" onClick={() => setGroupe(null)} aria-label="Fermer la liste" title="Fermer la liste">×</button>
                    </span>
                    {liste && !liste.erreur && (
                      <span className="ft-hint">{nb(liste.nb)} OR{liste.nb > liste.max ? ` · ${liste.max} premiers affichés` : ''}</span>
                    )}
                  </div>
                  <div className="vt-card-b vt-tbl-wrap ft-liste">
                    {!liste ? <p className="ft-vide">Chargement…</p>
                      : liste.erreur ? <p className="ft-vide">Liste indisponible.</p> : (
                        <table className="vt-tbl tbl-dense">
                          <thead><tr>
                            <th className="l">Société</th><th className="l">N° OR</th><th>Facturé le</th>
                            <th className="l">Atelier</th><th className="l">Réceptionnaire</th><th className="l">Marque</th>
                            {!typeDetail && <th className="l">Type</th>}
                            {(!typeDetail || typeDetail === 'Cession') && <th className="l">Destinataire</th>}
                            <th>M.O.</th><th>Pièces</th><th>Trav. ext.</th><th className="tot">Total</th>
                          </tr></thead>
                          <tbody>
                            {liste.ors.map((o) => (
                              <tr key={`${o.societe}-${o.numOr}`}>
                                <td className="l">{o.societe}</td>
                                <td className="l">{o.numOr || '—'}</td>
                                <td>{dateFr(o.date)}</td>
                                <td className="l">{o.atelier}</td>
                                <td className="l">{o.receptionnaire}</td>
                                <td className="l">{o.marque}</td>
                                {!typeDetail && <td className="l">{o.type}</td>}
                                {(!typeDetail || typeDetail === 'Cession') && <td className="l">{o.destinataire || '—'}</td>}
                                <td>{o.mo ? eur(o.mo) : '·'}</td><td>{o.pieces ? eur(o.pieces) : '·'}</td><td>{o.te ? eur(o.te) : '·'}</td>
                                <td className="tot">{eur(o.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </main>
      {Panneau}
    </>
  );
}
